import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { NotificacaoCanal, NotificacaoCanalMap } from "@workflow/types";
import { notificacoesAdminQueries } from "@/services/api/queries/notificacoes-admin.queries";
import { PeriodoResumoNotificacao } from "@/types/notificacao-log.type";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";

import { CANAL_CONFIG, CANAL_DESCONHECIDO_CONFIG, STATUS_CHART_CONFIG } from "./notificacao-visual";

/** Um canal e sua fatia do total do período — a régua rápida de "por onde saiu". */
function CanalStatCard({
  canal,
  total,
  percentual,
}: {
  canal: string;
  total: number;
  percentual: number;
}) {
  const config = CANAL_CONFIG[canal as NotificacaoCanal] ?? CANAL_DESCONHECIDO_CONFIG;
  const label = NotificacaoCanalMap[canal as NotificacaoCanal] ?? "Outro";
  const Icon = config.icon;

  return (
    <div className={cn("min-w-[150px] flex-1 rounded-lg border p-3", config.className)}>
      <div className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-xl font-bold tabular-nums">{total}</span>
        <span className="text-xs font-medium opacity-70">{percentual}%</span>
      </div>
    </div>
  );
}

export function NotificacaoEnviosOverview({ dias }: { dias: PeriodoResumoNotificacao }) {
  const { data, isLoading } = useQuery(notificacoesAdminQueries.logsResumoPorCanal(dias));

  const tendenciaDiaria = useMemo(() => {
    const porDia = new Map<string, { dia: string } & Record<string, string | number>>();
    for (const row of data ?? []) {
      const entry = porDia.get(row.dia) ?? { dia: row.dia };
      entry[row.status] = Number(entry[row.status] ?? 0) + row.total;
      porDia.set(row.dia, entry);
    }
    return Array.from(porDia.values())
      .sort((a, b) => (a.dia < b.dia ? -1 : 1))
      .map((e) => ({ ...e, label: format(new Date(e.dia), "dd/MM", { locale: ptBR }) }));
  }, [data]);

  const totaisPorCanal = useMemo(() => {
    const porCanal = new Map<string, number>();
    let total = 0;
    for (const row of data ?? []) {
      porCanal.set(row.canal, (porCanal.get(row.canal) ?? 0) + row.total);
      total += row.total;
    }
    return Array.from(porCanal.entries())
      .map(([canal, count]) => ({
        canal,
        count,
        percentual: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!data || data.length === 0) return null;

  return (
    <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
        <p className="mb-3 text-sm font-semibold text-foreground">Tendência de envios</p>
        <ChartContainer config={STATUS_CHART_CONFIG} className="!aspect-auto h-52 w-full">
          <BarChart data={tendenciaDiaria} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} className="text-[11px]" />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={28}
              allowDecimals={false}
              className="text-[11px]"
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            {Object.keys(STATUS_CHART_CONFIG).map((status) => (
              <Bar
                key={status}
                dataKey={status}
                stackId="status"
                fill={`var(--color-${status})`}
                radius={2}
              />
            ))}
            <ChartLegend content={<ChartLegendContent />} />
          </BarChart>
        </ChartContainer>
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
        <p className="mb-3 text-sm font-semibold text-foreground">Por canal</p>
        <div className="flex flex-wrap gap-2">
          {totaisPorCanal.map(({ canal, count, percentual }) => (
            <CanalStatCard key={canal} canal={canal} total={count} percentual={percentual} />
          ))}
        </div>
      </div>
    </div>
  );
}
